const { flatMap } = require('../../utils/Array');
const config = require('../config.js');
const pools = require(`./pools-${config.blockchainId}.js`);
const allCoins = require(`../coins/coins-${config.blockchainId}.js`);
const validatePoolConfigs = require('./init-validation');
const REFERENCE_ASSETS = require('../reference-assets.js');

let areKavaPoolsLoaded;
let areCeloPoolsLoaded;

const defaultPoolTemplate = {
    dataIndex: undefined,
    id: undefined,
    routeOverride: undefined, // To access the pool via a different url instead of its id
    statsId: undefined, // Indicate the pool id used in the stats endpoint if it differs
    idAlias: undefined, // e.g. how 'y' and 'iearn' are sometimes interchangeable
    name: undefined,
    pageMetaData: {
        title: undefined, // Defaults to `${pool.name}`
        description: undefined, // Defaults to `A curve.finance portal for swapping ${pool.assets}`
    },
    lpTokenInfo: {
        name: undefined,
        symbol: undefined,
    },
    coingeckoInfo: {
        id: undefined, // Must be defined
        symbol: undefined, // Must be defined
        referenceAssetId: 'dollar',
    },
    assets: undefined,
    // By default, the `coins` array is used to display a pool's assets. Setting this to true
    // will instead use the `assets` string to display the pool's assets.
    useAssetsStringForDisplayPurposes: false,
    coins: undefined,
    // In some pools (e.g. crveth), the pool can swap either of two coins, so the coin inside
    // `coinsInPlaceReplacements` must simply be used in the same spot as the one in `coins`
    // as if it was the same (e.g. with crveth, using eth as if it was weth at index 0)
    coinsInPlaceReplacements: [],
    underlyingCoins: undefined, // Must be set when isLendingPool=true
    metaCoins: undefined, // Must be set when isMetaPool=true
    referenceAsset: REFERENCE_ASSETS.USD, // Pool type, from the enum REFERENCE_ASSETS
    isLendingPool: false, // True when underlying assets are lent on Compound/Aave/else
    isModernLendingPool: false, // aave, saave, ib, and future lending pools
    isMetaPool: false,
    allowTradingEth: false, // Whether pool allows to trade actual eth (not just weth)
    isRiskier: false, // True for pools labeled as "innovation zone"
    riskLevel: undefined, // Must be defined if isRiskier = true
    isGaugeKilled: false, // When a gauge is killed, the contract will stop accruing rewards: invite users out by hiding apys and the deposit tab
    // Shape: { name, rewardTokenCoingeckoId, amountDataKey } for gaugeVersion === 1
    // Shape: { name, rewardTokenCoingeckoId, amountDataKey, rewardTokenAddress, rewardTokenDecimals } for gaugeVersion === 2
    additionalRewards: [],
    // Same as additionalRewards, only those rewards aren't actively distributed anymore.
    // Those old rewards aren't displayed in the pool list anymore, but this info is used
    // to retrieve claimable amounts for users who partook in these.
    // Shape: { name, rewardTokenCoingeckoId } for gaugeVersion === 1
    // Shape: { name, rewardTokenCoingeckoId, rewardTokenAddress, rewardTokenDecimals } for gaugeVersion === 2
    oldAdditionalRewards: [],
    hasNoGauge: false,
    gaugeVersion: null,
    isPendingGaugeVoteToStartCrvRewards: false,
    expectedCrvRewardsStart: null,
    addresses: {
        swap: null,
        lpToken: null,
        old_gauge: null,
        gauge: null,
        deposit: null,
        stakingRewards: null,
        adapter: null,
        migrator: null //if pool is being migrated to something else
    },
    hasAMultiplier: true,
    isOldPool: false,
    hasNoCRVRewards: false, //for pools that may have a gauge but no CRV Rewards like on Polygon/Matic
    poolType: 'stable', //stable or crypto
    isFakePlainPool: false, //this is a hacky bit to have the crypto pool play off as plain even though it is meta lending and has a zap
    isForexMetaPool: false, //some calls may be different on crypto meta pools throwing an error in callCryptoPoolData
    isPlainCryptoPool: false, //so some specfici crypto calls dont fail
    isStablePoolWithSpecialUnpeggedAssets: false,
    isBroken: false,
};

const augmentPoolData = (poolData) => poolData.map((pool) => ({
    ...defaultPoolTemplate,
    ...pool,
    // Default to 1 for pools with gauges
    gaugeVersion: (pool.gaugeVersion || pool.hasNoGauge) ? pool.gaugeVersion : 1,
    coingeckoInfo: { // Deep merge
        ...defaultPoolTemplate.coingeckoInfo,
        ...pool.coingeckoInfo,
    },
    addresses: { // Deep merge
        ...defaultPoolTemplate.addresses,
        ...pool.addresses,
    },
    pageMetaData: { // Deep merge
        title: pool.name,
        description: `A curve.finance portal for swapping ${pool.assets}`,
        ...pool.pageMetaData,
    },
    get containsSynthCoin() {
        return (
            pool.coins.some(({ isSynth }) => isSynth)
            // Mapping for metapools not available yet in synth swaps, uncomment below + delete last line when it is
            // || !!pool.underlyingCoins?.some(({ isSynth }) => isSynth)
            // || !!pool.metaCoins?.some(({ isSynth }) => isSynth)
            && !pool.isMetaPool
        );
    },
    get containsAaveCoin() {
        return (
            pool.coins.some(({ wrappedCoinType }) => wrappedCoinType === 'aave')
        );
    },
    get containsCompoundCoin() {
        return (
            pool.coins.some(({ wrappedCoinType }) => wrappedCoinType === 'compound')
        );
    },
    get containsYearnBasedCoin() {
        return (
            pool.coins.some(({ wrappedCoinType }) => wrappedCoinType && wrappedCoinType.startsWith('iearn'))
        );
    },
}));

const augmentedPools = augmentPoolData(pools).sort((a, b) => (
    a.dataIndex < b.dataIndex ? -1 :
        a.dataIndex > b.dataIndex ? 1 : 0
));

validatePoolConfigs(augmentedPools);

const hasMainPoolsEndpoint = !!config.mainPoolsEndpoint;
const hasCryptoPoolsEndpoint = !!config.cryptoPoolsEndpoint;
const hasFactoryPools = !!config.factoryRegistryAddress;
const hasFactoryV2Pools = !!config.factoryV2RegistryAddress;
const hasFactoryCryptoPools = !!config.factoryCryptoRegistryAddress;

let areMainPoolsAugmented = false;
let areCryptoPoolsAugmented = false;
let factoryPools;
let factoryV2Pools;
let factoryCryptoPools;

// Fake plain pools are meta pools (consisting of one curve lp token + plain tokens), being
// handled by the front-end as regular plain pools. Hence the front-end expects e.g. 5 coins
// when the pool has in fact e.g. 3 coins. This utility makes a few assumptions in order to
// map additional coin data to the corresponding pool data, since it can't simply rely on indices.
const fakePlainPoolSpecialCoinsAugmentation = (poolCoins, additionalPoolData, augmentedPools) => {
    const coinEquivalents = new Map(
        config.blockchainId === 'avalanche' ? [
            [allCoins.wbtc.address.toLowerCase(), allCoins.avWBTC],
            [allCoins.weth.address.toLowerCase(), allCoins.avWETH],
        ] : config.blockchainId === 'polygon' ? [
            [allCoins.wbtc.address.toLowerCase(), allCoins.amwbtc],
            [allCoins.weth.address.toLowerCase(), allCoins.amweth],
        ] : []
    )
    // The lp coin is the coin not matching any of the plain coins in the ui config
    const lpCoin = additionalPoolData.coins.find(({ address }) => !poolCoins.some((poolCoin) => poolCoin.address.toLowerCase() === address.toLowerCase()));
    const lpCoinPool = augmentedPools.find(({ addresses: { lpToken } }) => (lpToken && lpToken.toLowerCase()) === lpCoin.address.toLowerCase());
    const lpCoinPoolCoinsProportions = lpCoinPool.coins.map(({ decimals, poolBalance, usdPrice }) => (
        usdPrice * poolBalance / decimals / lpCoinPool.usdTotal
    ));

    // Match on address instead of index
    return poolCoins.map((poolCoin) => {
        let estimatedCoinData;
        const matchingCoin = additionalPoolData.coins.find(({ address }) => (
            address.toLowerCase() === poolCoin.address.toLowerCase() ||
            coinEquivalents.get(poolCoin.address.toLowerCase())?.address?.toLowerCase() === address.toLowerCase()
        ));

        if (!matchingCoin) {
            const coinFromLpCoinPool = (lpCoinPool.underlyingCoins || lpCoinPool.coins).find(({ address }) => (
                address.toLowerCase() === poolCoin.address.toLowerCase() ||
                coinEquivalents.get(address.toLowerCase())?.address?.toLowerCase() === poolCoin.address.toLowerCase()
            ));
            const coinFromLpCoinPoolIndex = (lpCoinPool.underlyingCoins || lpCoinPool.coins).indexOf(coinFromLpCoinPool);

            const coinFromLpCoinPoolUsdPrice = (
                coinFromLpCoinPool.usdPrice ||
                lpCoinPool.coins[coinFromLpCoinPoolIndex].usdPrice // Approximation for fake plain pools, price is incorrect but close to reality, it assumes coin price === underlying coin price
            );

            estimatedCoinData = {
                ...coinFromLpCoinPool,
                poolBalance: lpCoin.poolBalance / (10 ** lpCoin.decimals) * coinFromLpCoinPool.decimals * lpCoinPoolCoinsProportions[coinFromLpCoinPoolIndex],
                usdPrice: coinFromLpCoinPoolUsdPrice,
            };
        }

        return {
            ...(matchingCoin || estimatedCoinData),
            ...poolCoin,
        };
    });
};

if (process.env.CLI_SCRIPT !== 'build-icons') {
    setTimeout(async () => {
        const { default: getMainPools } = require('../../utils/data/getMainPools');
        const { default: getCryptoPools } = require('../../utils/data/getCryptoPools');
        const { default: getFactoryPools } = require('../../utils/data/getFactoryPools');
        const { default: getFactoryV2Pools } = require('../../utils/data/getFactoryV2Pools');
        const { default: getFactoryCryptoPools } = require('../../utils/data/getFactoryCryptoPools');
        const { default: getFactoryV2Gauges } = require('../../utils/data/getFactoryV2Gauges');

        const [
            mainPoolsData,
            cryptoPoolsData,
            factoryPoolsData,
            factoryV2PoolsData,
            factoryCryptoPoolsData,
            factoryV2Gauges,
        ] = await Promise.all([
            hasMainPoolsEndpoint ? getMainPools() : [],
            hasCryptoPoolsEndpoint ? getCryptoPools() : [],
            hasFactoryPools ? getFactoryPools() : [],
            hasFactoryV2Pools ? getFactoryV2Pools() : [],
            hasFactoryCryptoPools ? getFactoryCryptoPools() : [],
            getFactoryV2Gauges(),
        ]);

        // Augment main pools info (add usdTotal, and for some pools attach a facto gauge)
        augmentedPools.forEach((pool, i) => {
            const additionalMainPoolData = mainPoolsData.find(({ address }) => (
                address.toLowerCase() === pool.addresses.swap.toLowerCase()
            ));

            if (additionalMainPoolData) {
                augmentedPools[i] = {
                    ...pool,
                    usdTotal: additionalMainPoolData.usdTotal,
                    isBroken: additionalMainPoolData.isBroken,
                    additionalRewards: pool.additionalRewards.map((r, i) => ({
                        ...(additionalMainPoolData.gaugeRewards?.[i] ?? {}),
                        ...r,
                    })),
                    // additionalRewards: additionalMainPoolData.gaugeRewards || [],
                    lpTokenInfo: {
                        ...(pool.lpTokenInfo || {}),
                        totalSupply: additionalMainPoolData.totalSupply / 1e18,
                    },
                    coins: (
                        pool.isFakePlainPool ?
                            fakePlainPoolSpecialCoinsAugmentation(pool.coins, additionalMainPoolData, augmentedPools) :
                            pool.coins.map((coin, i) => ({
                                ...additionalMainPoolData.coins[i],
                                ...coin,
                            }))
                    ),
                    underlyingCoins: (
                        pool.metaCoins ? additionalMainPoolData.underlyingCoins : pool.underlyingCoins
                    ),
                };
            } else {
                const additionalCryptoPoolData = cryptoPoolsData.find(({ address }) => (
                    address.toLowerCase() === pool.addresses.swap.toLowerCase()
                ));

                if (additionalCryptoPoolData) {
                    // console.log('augmenting (crypto)', pool.id, additionalCryptoPoolData.usdTotal, additionalCryptoPoolData.coins)
                    augmentedPools[i] = {
                        ...pool,
                        usdTotal: additionalCryptoPoolData.usdTotal,
                        isBroken: additionalCryptoPoolData.isBroken,
                        // additionalRewards: additionalCryptoPoolData.gaugeRewards || [],
                        additionalRewards: pool.additionalRewards.map((r, i) => ({
                            ...(additionalCryptoPoolData.gaugeRewards?.[i] ?? {}),
                            ...r,
                        })),
                        lpTokenInfo: {
                            ...(pool.lpTokenInfo || {}),
                            totalSupply: additionalCryptoPoolData.totalSupply / 1e18,
                        },
                        coins: (
                            pool.isFakePlainPool ?
                                fakePlainPoolSpecialCoinsAugmentation(pool.coins, additionalCryptoPoolData, augmentedPools) :
                                pool.coins.map((coin, i) => ({
                                    ...additionalCryptoPoolData.coins[i],
                                    ...coin,
                                }))
                        ),
                        underlyingCoins: (
                            pool.metaCoins ? additionalCryptoPoolData.underlyingCoins : pool.underlyingCoins
                        ),
                    };
                }
            }

            if (config.blockchainId !== 'ethereum') {
                // Augment main pools data on sidechains: if a facto gauge was deployed for a main
                // pool, update its gauge address with the newest one
                const matchingFactoGauge = factoryV2Gauges.find(({ swap_token }) => (
                    swap_token.toLowerCase() === pool.addresses.lpToken.toLowerCase()
                ));
                if (matchingFactoGauge && matchingFactoGauge.gauge.toLowerCase() !== augmentedPools[i].addresses.gauge.toLowerCase()) {
                    augmentedPools[i] = {
                        ...augmentedPools[i],
                        addresses: {
                            ...augmentedPools[i].addresses,
                            gauge: matchingFactoGauge.gauge,
                            old_gauge: augmentedPools[i].addresses.gauge,
                            ...(augmentedPools[i].addresses.old_gauge ? { old_gauge_2: augmentedPools[i].addresses.old_gauge } : {}),
                        },
                        gaugeVersion: 2,
                        hasNoGauge: false,
                        hasNoCRVRewards: false,
                        rewardsNeedNudging: matchingFactoGauge.rewardsNeedNudging,
                        areCrvRewardsStuckInBridge: matchingFactoGauge.areCrvRewardsStuckInBridge,
                        additionalRewards: (matchingFactoGauge.extraRewards.map(({
                            apy,
                            gaugeAddress,
                            decimals,
                            name,
                            symbol,
                            tokenAddress,
                            tokenPrice,
                        }) => ({
                            key: `reward-${gaugeAddress.toLowerCase()}-${tokenAddress.toLowerCase()}`,
                            name: symbol,
                            rewardTokenAddress: tokenAddress,
                            rewardTokenDecimals: decimals,
                            // rewardTokenCoingeckoId: 'curve-dao-token',
                            rewardContract: gaugeAddress,
                        })) || []),
                        oldAdditionalRewards: augmentedPools[i].additionalRewards || [],
                        oldAdditionalRewardsAreForGauge: augmentedPools[i].additionalRewards.length > 0 ? augmentedPools[i].addresses.gauge : undefined,
                        oldAdditionalRewardsGaugeVersion: augmentedPools[i].gaugeVersion,
                        isMainPoolWithFactoGauge: true,
                    };
                }
            }
        });
        areMainPoolsAugmented = true;
        areCryptoPoolsAugmented = true;

        factoryPools = augmentPoolData(factoryPoolsData.map(({
            id,
            name,
            coins,
            address,
        }) => {
            const lpToken = coins[coins.length - 1];
            const basePoolId = (
                lpToken.symbol === '3Crv' ? '3pool' :
                    lpToken.symbol === 'sbtcCrv' ? 'sbtc' :
                        undefined
            );
            const basePool = getById(basePoolId);

            const depositZapAddress = (lpToken.symbol == '3pool') ? '0xA79828DF1850E8a3A3064576f380D90aECDD3359' : '0x7abdbaf29929e7f8621b757d2a7c04d78d633834'

            return {
                dataIndex: null,
                id,
                name,
                lpTokenInfo: {
                    name: lpToken.symbol,
                    symbol: lpToken.symbol,
                },
                isMetaPool: true,
                coins, // This isn't an array of `coins` objects like the regular pool configs used, but they have similar props
                metaCoins: basePool.coins,
                addresses: {
                    swap: address,
                    lpToken: address,
                    deposit: depositZapAddress, //0x7abdbaf29929e7f8621b757d2a7c04d78d633834
                },
                hasNoGauge: true,
                isFactoryOnlyPool: true,
                factoryVersion: 1,
            };
        }));

        /**
         * Allows to retrieve either main pools or facto pools data without factory data being
         * fully initialized yet; useful when a facto pool is a meta pool using another facto
         * pool as a base pool (i.e. when facto pools haven't been added to the main pools object
         * yet, but we do need access to that base pool's data).
         * Returns these properties only for facto pools: id name coins underlyingCoins
         */
        const safeGetPoolById = (poolId) => {
            if (!poolId) return null;

            const isFactoV2Pool = poolId.startsWith('factory-v2');
            if (!isFactoV2Pool) return getById(poolId);

            const poolData = factoryV2PoolsData.find(({ id }) => id === poolId);
            const transformToMainPoolDecimalsFormat = (coins) => (
                coins?.map(({ decimals, ...coin }) => ({
                    ...coin,
                    decimals: (10 ** decimals),
                }))
            );
            return {
                id: poolId,
                name: poolData.name,
                coins: transformToMainPoolDecimalsFormat(poolData.coins),
                underlyingCoins: transformToMainPoolDecimalsFormat(poolData.underlyingCoins),
            };
        };
        factoryV2Pools = augmentPoolData(factoryV2PoolsData.map(({
            id,
            name,
            coins,
            address,
            implementation,
            totalSupply,
            usdTotal,
            gauge,
            gaugeAddress,
            gaugeRewards,
            ...rest
        }) => {
            const isMetaPool = (
                implementation === 'v1metausd' ||
                implementation === 'metausd' ||
                implementation === 'metausdbalances' ||
                implementation === 'v1metabtc' ||
                implementation.startsWith('metabtc') ||
                implementation.startsWith('metabtcbalances') ||
                implementation.startsWith('metausd') ||
                implementation.startsWith('metausdbalances')
            );


            const isUsdMetaPool = isMetaPool && (implementation.startsWith('metausd') || implementation.startsWith('v1metausd'));
            const isBtcMetaPool = isMetaPool && (implementation.startsWith('metabtc') || implementation.startsWith('v1metabtc'));

            const metaUsdBasePool = (
                (config.factoryV2MetaUsd2BasePoolId && implementation.endsWith(`-${config.factoryV2MetaUsd2BasePoolId}`)) ? safeGetPoolById(config.factoryV2MetaUsd2BasePoolId) :
                    config.factoryV2MetaUsdBasePoolId ? safeGetPoolById(config.factoryV2MetaUsdBasePoolId) :
                        undefined
            );

            const metaBtcBasePool = (
                (config.factoryV2MetaBtc3BasePoolId && implementation.endsWith(`-${config.factoryV2MetaBtc3BasePoolId}`)) ? safeGetPoolById(config.factoryV2MetaBtc3BasePoolId) :
                    (config.factoryV2MetaBtc2BasePoolId && implementation.endsWith(`-${config.factoryV2MetaBtc2BasePoolId}`)) ? safeGetPoolById(config.factoryV2MetaBtc2BasePoolId) :
                        config.factoryV2MetaBtcBasePoolId ? safeGetPoolById(config.factoryV2MetaBtcBasePoolId) :
                            undefined
            );

            const lpTokenInfo = (
                isUsdMetaPool ? metaUsdBasePool.lpTokenInfo :
                    isBtcMetaPool ? metaBtcBasePool.lpTokenInfo :
                        undefined
            );

            const basePool = (
                isUsdMetaPool ? metaUsdBasePool :
                    isBtcMetaPool ? metaBtcBasePool :
                        undefined
            );

            const depositZapAddress = (
                (isUsdMetaPool && metaUsdBasePool.id === config.factoryV2MetaUsd2BasePoolId) ? config.factoryV2MetaUsd2DepositZapAddress :
                    (isBtcMetaPool && metaBtcBasePool.id === config.factoryV2MetaBtc3BasePoolId) ? config.factoryV2MetaBtc3DepositZapAddress :
                        isUsdMetaPool ? config.factoryV2MetaUsdDepositZapAddress :
                            config.factoryV2MetaBtcDepositZapAddress
            );

            const isMetaPoolAndTwoLevelsDeep = (
                isMetaPool &&
                basePool &&
                basePool.underlyingCoins
            );

            return {
                ...rest,
                dataIndex: null,
                id,
                name,
                lpTokenInfo,
                isMetaPool,
                isFactoMetaPoolAndTwoLevelsDeep: (isMetaPool && isMetaPoolAndTwoLevelsDeep),
                coins, // This isn't an array of `coins` objects like the regular pool configs used, but they have similar props
                metaCoins: (
                    (isMetaPool && isMetaPoolAndTwoLevelsDeep) ? basePool.underlyingCoins :
                        (isMetaPool && !isMetaPoolAndTwoLevelsDeep) ? basePool.coins :
                            undefined
                ),
                addresses: {
                    swap: address,
                    lpToken: address,
                    deposit: isMetaPool ? depositZapAddress : null,
                    gauge: gauge || gaugeAddress || undefined,
                    // Override for a facto pool that had a custom gauge, now retired
                    old_gauge: (
                        (config.blockchainId === 'avalanche' && id === 'factory-v2-30') ?
                            '0xA4106e1206d313a86051c6B5bc475181a81Dbc0f' :
                            undefined
                    ),
                },
                hasNoGauge: !(gaugeAddress || gauge),
                gaugeVersion: (gaugeAddress || gauge) ? 2 : undefined,
                isFactoryOnlyPool: true,
                factoryVersion: 2,
                totalSupply,
                usdTotal,
                referenceAsset: (
                    (implementation === 'plain2eth' || implementation === 'plain3eth' || implementation === 'plain4eth') ? REFERENCE_ASSETS.ETH :
                        isBtcMetaPool ? REFERENCE_ASSETS.BTC :
                            isUsdMetaPool ? REFERENCE_ASSETS.USD :
                                REFERENCE_ASSETS.USD // this might as well be REFERENCE_ASSETS.OTHER but it'll be incorrect anyway, so might as well have a sane default, don't hesitate to change if it's not optimal
                ),
                additionalRewards: gaugeRewards || [], // All additionalRewards for facto pools on eth are retrieved automatically by the api
            };
        }));

        factoryCryptoPools = augmentPoolData(factoryCryptoPoolsData.map(({
            id,
            name,
            coins,
            address,
            lpTokenAddress,
            totalSupply,
            usdTotal,
            gauge,
            gaugeAddress,
            gaugeRewards,
            implementation,
            ...rest
        }) => {
            const isMetaPool = (
                implementation.startsWith('metacrypto')
            );

            const metaCryptoBasePool = (
                (config.factoryCryptoMetaBasePoolId && implementation === 'metacrypto') ? getById(config.factoryCryptoMetaBasePoolId) :
                    (config.factoryCryptoMeta2BasePoolId && implementation === `metacrypto-${config.factoryCryptoMeta2BasePoolId}`) ? getById(config.factoryCryptoMeta2BasePoolId) :
                        undefined
            );

            const lpTokenInfo = (
                isMetaPool ? metaCryptoBasePool.lpTokenInfo :
                    undefined
            );

            const basePool = (
                isMetaPool ? metaCryptoBasePool :
                    undefined
            );

            const depositZapAddress = (
                (isMetaPool && implementation === 'metacrypto') ? config.factoryCryptoMetaDepositZapAddress :
                    (isMetaPool && implementation === `metacrypto-${config.factoryCryptoMeta2BasePoolId}`) ? config.factoryCryptoMeta2DepositZapAddress :
                        null
            );

            return {
                ...rest,
                dataIndex: null,
                id,
                name,
                referenceAsset: REFERENCE_ASSETS.CRYPTO,
                cryptoPool: true,
                isPlainCryptoPool: true,
                isFakePlainMetaPool: basePool?.isFakePlainPool,
                basePool: basePool,
                // lpTokenInfo: {
                //     name: `${name.replace('Curve.finance Factory Crypto Pool: ', '').replace('/', '')}Curve`,
                //     symbol: `${name.replace('Curve.finance Factory Crypto Pool: ', '').replace('/', '')}Crv`,
                // },
                lpTokenInfo,
                isMetaPool,
                coins, // This isn't an array of `coins` objects like the regular pool configs used, but they have similar props
                metaCoins: (
                    isMetaPool ? basePool.coins :
                        undefined
                ),
                assets: coins.map(({ address, symbol }) => (
                    address.toLowerCase() === allCoins[config.nativeAssetErc20WrapperId].address.toLowerCase() ? allCoins[config.nativeAssetId].symbol : symbol
                )).join('+'),
                useAssetsStringForDisplayPurposes: true,
                // coinsInPlaceReplacements: coins.map(({ address }) => (
                //     address.toLowerCase() === allCoins[config.nativeAssetErc20WrapperId].address.toLowerCase() ? allCoins[config.nativeAssetId] : undefined
                // )),
                allowTradingEth: coins.some(({ address }) => address.toLowerCase() === allCoins[config.nativeAssetErc20WrapperId].address.toLowerCase()),
                addresses: {
                    swap: address,
                    lpToken: lpTokenAddress,
                    gauge: gauge || gaugeAddress || undefined,
                    deposit: isMetaPool ? depositZapAddress : null,
                },
                hasNoGauge: !(gaugeAddress || gauge),
                gaugeVersion: (gaugeAddress || gauge) ? 2 : undefined,
                isFactoryOnlyPool: true,
                factoryVersion: 2,
                totalSupply,
                usdTotal,
                additionalRewards: gaugeRewards || [], // All additionalRewards for facto pools on eth are retrieved automatically by the api
            };
        }));


        if (config.blockchainId === 'kava' || config.blockchainId === 'celo') {
            // Replace the hacky first hardcoded pool (not a main pool, but
            // this old codebase expects at least one hardcoded main pool in
            // many places, so we're simulating one.
            const [firstFactoPool] = factoryV2Pools.splice(0, 1);
            Array.from(Object.keys(augmentedPools[0])).map((key) => {
                augmentedPools[0][key] = undefined;
            });
            Array.from(Object.entries(firstFactoPool)).map(([key, val]) => {
                augmentedPools[0][key] = val;
            });
            augmentedPools[0].name = '3pool';

            areKavaPoolsLoaded = true;
            areCeloPoolsLoaded = true;
        }

        augmentedPools.push(...factoryV2Pools);
        augmentedPools.push(...factoryCryptoPools);
    }, 0);
}

const poolIds = augmentedPools.map(({ id }) => id);
const poolIdsWithAliases = flatMap(augmentedPools, ({ id, idAlias }) => [id, idAlias]).filter((o) => !!o);
const poolIdsWithAliasesAndRouteOverrides = flatMap(augmentedPools, ({ id, idAlias, routeOverride }) => [id, idAlias, routeOverride]).filter((o) => !!o);
const getPoolGauges = () => augmentedPools.map(({ addresses: { gauge } }) => gauge).filter((gauge) => !!gauge);
const getNonFactoPoolGauges = () => augmentedPools.filter(({ id, isFactoryOnlyPool }) => {
    const isConsideredNonFactoPool = (
        !isFactoryOnlyPool ||
        (config.blockchainId === 'kava' && id === 'factory-v2-0') ||
        (config.blockchainId === 'celo' && id === 'factory-v2-0')
    );

    return isConsideredNonFactoPool;
}).map(({ addresses: { gauge } }) => gauge).filter((gauge) => !!gauge);
const getNonFactoPools = () => (
    augmentedPools.filter(({ id, isFactoryOnlyPool }) => {
        const isConsideredNonFactoPool = (
            !isFactoryOnlyPool ||
            (config.blockchainId === 'kava' && id === 'factory-v2-0') ||
            (config.blockchainId === 'celo' && id === 'factory-v2-0')
        );

        return isConsideredNonFactoPool;
    })
);

const exp = augmentedPools;
exp.getPools = () => augmentedPools;
exp.poolIds = poolIds;
exp.poolIdsWithAliases = poolIdsWithAliases;
exp.poolIdsWithAliasesAndRouteOverrides = poolIdsWithAliasesAndRouteOverrides;
exp.getPoolGauges = getPoolGauges;
exp.getNonFactoPoolGauges = getNonFactoPoolGauges;
exp.getNonFactoPools = getNonFactoPools;
exp.getFactoryV2Pools = () => factoryV2Pools;
exp.getFactoryCryptoPools = () => hasFactoryCryptoPools ? factoryCryptoPools : [];

const getById = (id, throwIfNotFound = true) => {
    if (!id || id === 'Synthetix') return null;

    const res = (
        augmentedPools.find((pool) => id === pool.id) ||
        augmentedPools.find((pool) => id === pool.idAlias) // Check for id aliases if no match for any pool id
    );

    if (res) return res;

    if (id.startsWith('factory-v2-')) {
        if (!factoryV2Pools) {
            if (throwIfNotFound) {
                throw new Error(`Tried to get a factory pool by id (${id}) when the factoryV2Pools object hasn't had time to populate yet, please defer that call to pools.getById()`);
            } else {
                return null;
            }
        }

        // Check for factory pools
        return factoryV2Pools.find((pool) => id === pool.id);
    }

    if (id.startsWith('factory-crypto-')) {
        if (!factoryCryptoPools) {
            if (throwIfNotFound) {
                throw new Error(`Tried to get a factory pool by id (${id}) when the factoryCryptoPools object hasn't had time to populate yet, please defer that call to pools.getById()`);
            } else {
                return null;
            }
        }

        // Check for factory pools
        return factoryCryptoPools.find((pool) => id === pool.id);
    }

    if (!factoryPools) {
        if (throwIfNotFound) {
            throw new Error(`Tried to get a factory pool by id (${id}) when the factoryPools object hasn't had time to populate yet, please defer that call to pools.getById()`);
        } else {
            return null;
        }
    }

    // Check for factory pools
    return factoryPools.find((pool) => id === `factory-${pool.id}`);
};
exp.getById = getById;

exp.findPoolForCoinsIds = (coinIdA, coinIdB) => augmentedPools.find(({ coins, underlyingCoins }) => (
    (coins.some(({ id }) => id === coinIdA) || (underlyingCoins && underlyingCoins.some(({ id }) => id === coinIdA))) &&
    (coins.some(({ id }) => id === coinIdB) || (underlyingCoins && underlyingCoins.some(({ id }) => id === coinIdB)))
));

exp.findSynthPoolWithCoinId = (coinId) => augmentedPools.find((pool) => ((
    pool.coins.some(({ id }) => id === coinId) ||
    (pool.underlyingCoins && pool.underlyingCoins.some(({ id }) => id === coinId))
) && pool.containsSynthCoin));

exp.findPoolForSwapAddress = (swapAddress) => {
    const lcSwapAddress = swapAddress.toLowerCase();
    return augmentedPools.find(({ addresses }) => (
        addresses.swap.toLowerCase() === lcSwapAddress ||
        (addresses.lpToken && addresses.lpToken.toLowerCase()) === lcSwapAddress ||
        (addresses.deposit && addresses.deposit.toLowerCase() === lcSwapAddress)
    ));
};

exp.isFactoryPoolDataLoaded = () => (
    areMainPoolsAugmented &&
    areCryptoPoolsAugmented &&
    typeof factoryPools !== 'undefined' &&
    (typeof factoryV2Pools !== 'undefined' || (config.blockchainId === 'kava' && areKavaPoolsLoaded) || (config.blockchainId === 'celo' && areCeloPoolsLoaded)) &&
    typeof factoryCryptoPools !== 'undefined'
);

module.exports = exp;
